import { type InputHTMLAttributes, useId, useState } from 'react';

import { Icon } from '@/components/ui/Icon';
import { cn } from '@/utils/cn';

import { Field } from './Field';
import styles from './Field.module.css';
import { fieldDescriptionIds } from './field-ids';

export interface PasswordFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label: string;
  hint?: string | undefined;
  error?: string | undefined;
}

/** A password input with a show/hide toggle. */
export function PasswordField({
  label,
  hint,
  error,
  id,
  required,
  className,
  ...inputProps
}: PasswordFieldProps) {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const [visible, setVisible] = useState(false);

  return (
    <Field label={label} htmlFor={inputId} hint={hint} error={error} required={required}>
      <div className={styles.control}>
        <input
          id={inputId}
          type={visible ? 'text' : 'password'}
          required={required}
          aria-invalid={error ? true : undefined}
          aria-describedby={fieldDescriptionIds(inputId, hint, error)}
          className={cn(styles.input, styles.withTrailing, error && styles.invalid, className)}
          {...inputProps}
        />
        <button
          type="button"
          className={styles.trailingButton}
          aria-label={visible ? 'Hide password' : 'Show password'}
          aria-pressed={visible}
          onClick={() => {
            setVisible((current) => !current);
          }}
        >
          <Icon name={visible ? 'eye-off' : 'eye'} size={20} />
        </button>
      </div>
    </Field>
  );
}
